import React from 'react';
import { Flex, TextV2, Box, BoxProps } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { useHotelUserRating } from '@shared/hooks/useHotelUserRating';
import type { HotelRatingContext } from '@shared/hooks/useHotelUserRating';
import { useTransformRatingToCategory } from '@shared/hooks/useTransformRatingToCategory';
import { useCompactNumberFormatter } from '@shared/hooks/useCompactNumberFormatter';
import type { HotelArgs } from './HotelRatingV2.types';

interface HotelRatingV2Props
  extends BoxProps,
    Readonly<{
      hotel: HotelArgs;
      context: HotelRatingContext;
      variant?: 'small' | 'large' | 'mobileSmall' | 'leftAligned';
      showRatingCategory?: boolean;
      isSkipQuery?: boolean;
    }> {}

export const HotelRatingV2: React.FC<HotelRatingV2Props> = ({ hotel, context, variant, showRatingCategory = true, isSkipQuery, ...boxProps }) => {
  const { t } = useTranslation('planningAccommodations');
  const { hotelItem } = t('browse');
  const isSmall = variant === 'small';
  const isMobileSmall = variant === 'mobileSmall';
  const { formatCompactNumber } = useCompactNumberFormatter();
  const { reviewCount, validatedRating, ratingCategory, loading } = useHotelUserRating({
    hotel,
    context,
    skip: isSkipQuery || !!hotel?.rating,
  });

  const hotelRatingCategory = useTransformRatingToCategory(hotel?.rating);
  const ratingCategoryString = hotelRatingCategory || ratingCategory;

  const getFlexDirection = () => {
    if (isMobileSmall) {
      return 'column-reverse';
    }
    if (isSmall) {
      return 'row-reverse';
    }
    return 'row';
  };

  const getRatingBoxSize = () => {
    if (isSmall) {
      return pxToRem(36);
    }
    return pxToRem(40);
  };

  const getReviewCount = () => {
    if (hotel?.ratingCount) {
      return formatCompactNumber(hotel.ratingCount);
    }
    return formatCompactNumber(reviewCount || 0);
  };

  if (loading) {
    return (
      <Flex
        gap={5}
        width="fit-content"
        flexDirection={getFlexDirection()}
        alignItems={isMobileSmall ? 'flex-end' : 'center'}
        {...boxProps}
      >
        <Flex
          flexDirection="column"
          gap={1}
        >
          <Skeleton
            placeholder={
              <Box
                width={pxToRem(80)}
                height={pxToRem(16)}
                backgroundColor="mono3"
              >
                &nbsp;
              </Box>
            }
          />
          <Skeleton
            placeholder={
              <Box
                width={pxToRem(60)}
                height={pxToRem(14)}
                backgroundColor="mono3"
              >
                &nbsp;
              </Box>
            }
          />
        </Flex>
        <Skeleton
          placeholder={
            <Box
              width={getRatingBoxSize()}
              height={getRatingBoxSize()}
              backgroundColor="mono3"
              borderRadius={pxToRem(6)}
            >
              &nbsp;
            </Box>
          }
        />
      </Flex>
    );
  }

  if (!reviewCount && !validatedRating && !hotel.rating && !hotel.ratingCount) {
    return null;
  }

  const rating = hotel?.rating || validatedRating;

  return (
    <Flex
      gap={5}
      width="fit-content"
      flexDirection={getFlexDirection()}
      alignItems={isMobileSmall ? 'flex-end' : 'center'}
      {...boxProps}
    >
      <Flex
        color="white"
        alignItems="center"
        justifyContent="center"
        borderRadius={pxToRem(6)}
        width={getRatingBoxSize()}
        typographyVariant="button1"
        backgroundColor="newUITest1"
        height={getRatingBoxSize()}
      >
        {rating?.toFixed(1)}
      </Flex>
      <Flex
        flexDirection="column"
        gap={1}
        textAlign={isSmall || variant === 'leftAligned' ? 'start' : 'end'}
      >
        {showRatingCategory && (
          <TextV2
            typographyVariant="button1"
            color="mono14"
            whiteSpace="nowrap"
          >
            {ratingCategoryString}
          </TextV2>
        )}
        {(hotel?.ratingCount || reviewCount) && (
          <TextV2
            typographyVariant="label2"
            color="mono10"
            whiteSpace="nowrap"
          >
            {hotelItem.reviews({ reviews: getReviewCount() })}
          </TextV2>
        )}
      </Flex>
    </Flex>
  );
};
